import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';

import { db } from './firebase';
import {
  parseDetail,
  serializeDetail,
  type PlatformCheck,
  type ProposalCacheRecord,
  type ProposalCacheStore,
} from '../components/ExternalBrandProposals/proposalCachePolicy';

/**
 * Firestore side of the External Brand Proposals cache:
 * `external_brand_proposals/{slug}`, admin-only (rules: watchlock
 * firestore.rules). Doc shape:
 *
 *   { slug, maId, detailJson: string, fetchedAt: Timestamp,
 *     platformCheck?: { verdict, checkedAt: Timestamp, siteUrl } }
 *
 * Policy (freshness, fallback): components/ExternalBrandProposals/proposalCachePolicy.ts.
 */
const COLLECTION = 'external_brand_proposals';

function toDate(value: unknown): Date | null {
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  return null;
}

function toPlatformCheck(raw: unknown): PlatformCheck | null {
  const r = raw as { verdict?: unknown; checkedAt?: unknown; siteUrl?: unknown } | null;
  const checkedAt = toDate(r?.checkedAt);
  if (!r || typeof r.verdict !== 'string' || !checkedAt) return null;
  return {
    verdict: r.verdict as PlatformCheck['verdict'],
    checkedAt,
    siteUrl: typeof r.siteUrl === 'string' ? r.siteUrl : '',
  };
}

export const firestoreProposalCache: ProposalCacheStore = {
  async read(slug) {
    const snap = await getDoc(doc(db, COLLECTION, slug));
    if (!snap.exists()) return null;
    const data = snap.data();
    const detail = parseDetail(data.detailJson);
    const fetchedAt = toDate(data.fetchedAt);
    if (!detail || !fetchedAt) return null;
    return {
      slug,
      maId: typeof data.maId === 'string' ? data.maId : detail.id,
      detail,
      fetchedAt,
      platformCheck: toPlatformCheck(data.platformCheck),
    } satisfies ProposalCacheRecord;
  },
  async write(record) {
    await setDoc(
      doc(db, COLLECTION, record.slug),
      {
        slug: record.slug,
        maId: record.maId,
        detailJson: serializeDetail(record.detail),
        fetchedAt: Timestamp.fromDate(record.fetchedAt),
        ...(record.platformCheck
          ? {
              platformCheck: {
                verdict: record.platformCheck.verdict,
                checkedAt: Timestamp.fromDate(record.platformCheck.checkedAt),
                siteUrl: record.platformCheck.siteUrl,
              },
            }
          : {}),
      },
      { merge: true },
    );
  },
};

/** Persist one "Verify platform" result on the brand's cache doc. */
export async function saveProposalPlatformCheck(slug: string, check: PlatformCheck): Promise<void> {
  await setDoc(
    doc(db, COLLECTION, slug),
    {
      slug,
      platformCheck: {
        verdict: check.verdict,
        checkedAt: Timestamp.fromDate(check.checkedAt),
        siteUrl: check.siteUrl,
      },
    },
    { merge: true },
  );
}
